import { EditIcon, ViewIcon } from '@chakra-ui/icons'
import { Box, Collapse, Flex, IconButton, Text } from '@chakra-ui/react'
import { useCollection } from '@hb/react'
import {
  capitalizeFirstLetter,
  CoverageId,
  InsuranceCoverage,
  insurersCollection,
} from '@hb/shared'
import { useMemo } from 'react'
import { useField } from 'react-final-form'
import { InsuranceCoveragePreview } from '../../../Insurance/InsuranceCoveragePreview'
import { useFormWizard } from '../../FormWizard/context'
import { FormElement } from '../../Input'
import { useInsuranceInfoStep } from './InsuranceInfoStepContext'
import { PlanIsMedicaid } from './PlanIsMedicaid'

const CoverageComplete = ({
  coveragePropName,
  coverageId,
}: {
  coveragePropName: string
  coverageId: CoverageId
}) => {
  const {
    input: { value: coverageValue },
  } = useField(coveragePropName, { subscription: { error: true, value: true } })
  const { readOnly } = useFormWizard()

  const { step, setStep } = useInsuranceInfoStep()
  const { items: insuranceProviders } = useCollection(insurersCollection)

  return (
    <Collapse in={step !== `${coverageId}-coverage`}>
      <Flex w="100%" bg="white" p={2} borderRadius={4} border="1px solid" borderColor="gray.200">
        <Box flex={1}>
          <InsuranceCoveragePreview
            plan={coverageValue as InsuranceCoverage}
            allInsuranceProviders={insuranceProviders}
            size="lg"
            noBadge
          />
        </Box>
        <IconButton
          variant="ghost"
          borderRadius="full"
          onClick={() => setStep(`${coverageId}-coverage`)}
          icon={readOnly ? <ViewIcon /> : <EditIcon />}
          aria-label="Edit"
        />
      </Flex>
    </Collapse>
  )
}

// const MakePrimaryButton = () => {
//   const { insuranceInfoData, onSaveChanges } = useInsuranceInfoStage()

//   const [isLoading, setIsLoading] = useState(false)

//   const handleClick = useCallback(async () => {
//     setIsLoading(true)
//     const secondaryCoverage = insuranceInfoData?.secondaryCoverage
//     const primaryCoverage = insuranceInfoData?.primaryCoverage
//     if (!secondaryCoverage || !primaryCoverage) {
//       return
//     }
//     try {
//       await onSaveChanges({
//         primaryCoverage: { ...secondaryCoverage },
//         secondaryCoverage: { ...primaryCoverage },
//         // primaryIsMedicaid: !!insuranceInfoData?.secondaryIsMedicaid,
//         // secondaryIsMedicaid: !!insuranceInfoData?.primaryIsMedicaid,
//       })
//     } catch (e) {
//       console.error(e)
//     }
//     setIsLoading(false)
//   }, [onSaveChanges, insuranceInfoData])

//   return (
//     <ActionButton isLoading={isLoading} size="xs" onClick={handleClick}>
//       Make Primary
//     </ActionButton>
//   )
// }

const CoverageSelect = ({ label }: { label: CoverageId }) => {
  const { readOnly } = useFormWizard()
  const { step, primaryCoverage, secondaryCoverage } = useInsuranceInfoStep()

  const propName = useMemo(
    () => (label === 'primary' ? 'primaryCoverage' : 'secondaryCoverage'),
    [label],
  )

  const field = useMemo(() => {
    if (label === 'primary') {
      return primaryCoverage.field
    }
    return secondaryCoverage.field
  }, [label, primaryCoverage, secondaryCoverage])

  const isMedicaidSet = useMemo(
    () =>
      primaryCoverage.data?.isMedicaid !== undefined && primaryCoverage.data?.isMedicaid !== null,
    [primaryCoverage.data?.isMedicaid],
  )

  const isVisible = useMemo(() => {
    if (step === 'option' || step === 'option-detail') return false
    if (label === 'primary') {
      return primaryCoverage.hasCoverage
    }
    return secondaryCoverage.hasCoverage
  }, [label, primaryCoverage, secondaryCoverage, step])

  // const editingIsMedicaid = useMemo(() => {
  //   const currValue =
  //     label === 'primary' ? primaryCoverage?.data?.isMedicaid : secondaryCoverage?.data?.isMedicaid
  //   return coverageValue?.isMedicaid !== currValue
  // }, [label, primaryCoverage, secondaryCoverage, coverageValue])

  return (
    <Collapse unmountOnExit in={isVisible}>
      <Flex px={2} w="100%" flexFlow="column">
        <Flex align="center" width="100%" justifyContent="space-between">
          <Text fontWeight={500} fontSize="lg" fontFamily="Hero-New">
            {capitalizeFirstLetter(label)} Coverage
          </Text>
        </Flex>
        <Collapse unmountOnExit style={{ width: '100%' }} in={step === `${label}-coverage`}>
          <PlanIsMedicaid label={label} />
          <Collapse in={isMedicaidSet}>
            <Box px={1} pointerEvents={readOnly ? 'none' : 'auto'}>
              <FormElement depth={0} name={propName} field={field} readOnly={readOnly} />
            </Box>
          </Collapse>
        </Collapse>
        <CoverageComplete coveragePropName={propName} coverageId={label} />
      </Flex>
    </Collapse>
  )
}

export const PrimaryCoverageSelect = () => <CoverageSelect label="primary" />
export const SecondaryCoverageSelect = () => <CoverageSelect label="secondary" />
